<script setup lang="ts">
import {
    faMobileAlt,
    faMobileScreenButton,
    faDownload,
} from '@fortawesome/pro-solid-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import {
    faUsers,
    faLayerGroup,
    faSlidersH,
} from '@fortawesome/pro-duotone-svg-icons';
import { faBazaar } from '@static/customIcons';

const mobile = ref('');
const { loading, submit } = useSendAppDownloadLink(mobile);
const features = [
    {
        content: 'همراه همیشگی',
        icon: faUsers,
    },
    {
        content: 'مدیریت ساده‌تر',
        icon: faSlidersH,
    },
    {
        content: 'دسترسی یکپارچه به خدمات سفر',
        icon: faLayerGroup,
    },
];
function openWebAppModal() {
    const WebApplicationModal = defineAsyncComponent(
        () => import('@components/ui/WebApplicationModal.vue')
    );
    const { open } = useModal({
        component: WebApplicationModal,
        attrs: {},
    });
    open();
}
</script>
<template>
    <div id="mr-app" class="download-app-container">
        <div class="download-app-wrapper container">
            <div class="desktop-layout">
                <div class="iphone-section">
                    <img
                        src="/img/download-app/iPhone.png"
                        alt="اپلیکیشن مِستربلیط"
                        :width="250"
                        :height="278"
                    />
                </div>
                <div class="content-section">
                    <mr-typography
                        size="display4"
                        weight="700"
                        color="var(--gray-800)"
                    >
                        ساده‌تر با اپلیکیشن مِستربلیط
                    </mr-typography>
                    <div class="cta-row">
                        <div class="input-wrapper">
                            <mr-input
                                v-model="mobile"
                                :icon="faMobileAlt"
                                placeholder="شماره موبایل"
                                required
                                hide-description
                                @submit="submit"
                            />
                        </div>
                        <mr-button :loading @click="submit">
                            ارسال لینک دانلود
                        </mr-button>
                    </div>
                    <mr-typography size="large" color="var(--gray-800)">
                        در صورت استفاده از تلفن‌های همراه اندروید می‌توانید با
                        وارد کردن شماره‌ی خود، لینک دانلود اپلیکیشن رسمی
                        مِستربلیط را دریافت کنید.
                    </mr-typography>
                    <div class="features-row">
                        <div
                            v-for="feature in features"
                            :key="feature.content"
                            class="feature-item"
                        >
                            <mr-icon
                                :icon="feature.icon"
                                color="var(--blue-400)"
                            />
                            <mr-typography
                                size="large"
                                weight="500"
                                color="var(--cool-gray-900)"
                            >
                                {{ feature.content }}
                            </mr-typography>
                        </div>
                    </div>
                </div>
                <div class="cafebazaar-card">
                    <div class="qr-frame">
                        <img
                            src="/img/download-app/bazaar-landing.png"
                            alt="QR-code کافه بازار"
                            class="qr"
                        />
                    </div>
                    <mr-typography
                        size="large"
                        weight="500"
                        color="white"
                        class="bazaar"
                    >
                        مِستربلیط در کافه بازار
                        <mr-icon :icon="faBazaar" color="white" size="large" />
                    </mr-typography>
                    <mr-typography
                        size="x-large"
                        weight="500"
                        color="white"
                        align="center"
                    >
                        برای دانلود اسکن کنید.
                    </mr-typography>
                </div>

                <!-- Chevron sidebar (last = leftmost in RTL) -->
                <NuxtLink to="/app" class="titles-sidebar">
                    <mr-icon
                        :icon="faChevronLeft"
                        color="var(--cool-gray-900)"
                    />
                </NuxtLink>
            </div>

            <!-- Mobile Layout -->
            <div class="mobile-layout">
                <div class="mobile-header">
                    <mr-typography
                        size="x-large"
                        weight="700"
                        color="var(--gray-800)"
                    >
                        ساده‌تر با اپلیکیشن مِستربلیط
                    </mr-typography>
                </div>
                <NuxtLink class="mobile-features-link" to="/app">
                    <mr-typography
                        size="large"
                        weight="500"
                        color="var(--cool-gray-900)"
                    >
                        مزایای اپلیکیشن مِستربلیط
                    </mr-typography>
                    <div class="icon-wrapper">
                        <mr-icon
                            :icon="faChevronLeft"
                            size="large"
                            color="var(--cool-gray-900)"
                        />
                    </div>
                </NuxtLink>
                <mr-divider />
                <div class="mobile-phone-section">
                    <div class="mobile-phone-wrapper">
                        <div class="buttons">
                            <mr-button
                                link
                                target="_blank"
                                href="https://trc.metrix.ir/hujtsf/"
                                rel="nofollow noopener noreferrer"
                                variant="bordered"
                                :right-icon="faBazaar"
                                full-width
                            >
                                کافه بازار
                            </mr-button>
                            <mr-button
                                target="_blank"
                                link
                                full-width
                                href="https://trc.metrix.ir/kw680p/"
                                rel="nofollow noopener noreferrer"
                                :right-icon="faDownload"
                                variant="bordered"
                            >
                                دانلود مستقیم
                            </mr-button>
                            <mr-button
                                variant="bordered"
                                full-width
                                :right-icon="faMobileScreenButton"
                                @click="openWebAppModal()"
                            >
                                وب اپلیکیشن
                            </mr-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.download-app-container {
    width: 100%;
    padding-block: 24px;
    padding-inline: 12px;

    @include mobile-only($app-tablet-max-width) {
        padding: 0;
    }
    .download-app-wrapper {
        box-sizing: border-box;
        border: 2px solid usePalette($p-cool-gray, $p-200);
        border-radius: 12px;
        margin: 0 auto;
        overflow: hidden;
        padding: 40px;

        @include mobile-only($app-tablet-max-width) {
            border-radius: 12px;
            background: transparent;
            padding: 16px 8px 0;
            border: 2px solid usePalette($p-cool-gray, $p-200);
        }
    }
}
.desktop-layout {
    display: flex;
    gap: 50px;
    @include mobile-only($app-tablet-max-width) {
        display: none;
    }
    .iphone-section {
        flex-shrink: 0;
        display: flex;
        align-items: flex-end;
        align-self: flex-end;
        padding: 0;
        line-height: 0;
        margin-bottom: -40px;
        width: 250px;
    }
    .content-section {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 12px;
        flex: 1;
        .cta-row {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;

            .input-wrapper {
                flex: 1;
            }
        }
        .features-row {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            width: 100%;
            .feature-item {
                display: flex;
                align-items: center;
                gap: 8px;
            }
        }
    }
    .cafebazaar-card {
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 16px;
        gap: 8px;
        flex-shrink: 0;
        background: linear-gradient(180deg, #0f84fa 0%, #67b1fb 100%);
        border: 1px solid #66b3ff;
        border-radius: 16px;
        .qr-frame {
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            background: #ffffff;
            border: 1px solid #66b3ff;
            border-radius: 8px;
            .qr {
                width: 172px;
                height: 172px;
            }
        }
        .bazaar {
            display: flex;
        }
    }
    .titles-sidebar {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 48px;
        align-self: stretch;
        background: usePalette($p-cool-gray, $p-100);
        border-radius: 16px;
        flex-shrink: 0;
        border: none;
        cursor: pointer;
        text-decoration: none;
    }
}
.mobile-layout {
    display: none;

    @include mobile-only($app-tablet-max-width) {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
    }
    .mobile-header {
        width: 100%;
    }

    .mobile-features-link {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding-right: 16px;
        border-radius: 16px;
        height: 56px;
        cursor: pointer;
        background-color: usePalette($p-cool-gray, $p-100);
        .icon-wrapper {
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .mobile-phone-section {
        display: flex;
        justify-content: center;
        width: 100%;
        padding: 16px 16px 0;

        .mobile-phone-wrapper {
            width: 100%;
            background-image: url('/img/download-app/iPhone-mobile-version.png');
            background-repeat: no-repeat;
            background-position-x: center;
            height: 276px;
            display: flex;
            align-items: center;
            justify-content: center;
            .buttons {
                display: flex;
                flex-direction: column;
                gap: 12px;
                padding-top: 48px;
                justify-content: center;
                align-items: center;
                max-width: 200px;
                width: 100%;
            }
        }
    }
}
</style>
